// src/components/practice/writing/WritingWordCounter.jsx - ENHANCED VERSION
import React, { useMemo } from 'react';
import { Target } from 'lucide-react';

const WritingWordCounter = ({ text, wordLimit }) => {
    const currentCount = text ? text.trim().split(/\s+/).filter(Boolean).length : 0;

    // Enhanced parsing logic to handle all word limit formats
    const parsedLimit = useMemo(() => {
        // Default state if no valid limit is found
        const defaults = { type: 'none', target: 0, range: [0, 0], targetString: '' };

        console.log('WritingWordCounter - received wordLimit:', wordLimit); // Debug log

        if (!wordLimit || typeof wordLimit !== 'object') {
            return defaults;
        }

        // Handle minimum word count (e.g., { "min": 150 })
        if (typeof wordLimit.min === 'number') {
            return {
                type: 'min',
                target: wordLimit.min,
                range: [wordLimit.min, Infinity],
                targetString: `mind. ${wordLimit.min} Wörter`
            };
        }

        // Handle target word count (e.g., { "target": 80 })
        if (typeof wordLimit.target === 'number') {
            return {
                type: 'approx',
                target: wordLimit.target,
                range: [wordLimit.target * 0.9, wordLimit.target * 1.1],
                targetString: `ca. ${wordLimit.target} Wörter`
            };
        }

        // Handle range word count (e.g., { "range": [100, 150] })
        if (Array.isArray(wordLimit.range) && wordLimit.range.length === 2) {
            return {
                type: 'range',
                target: wordLimit.range[1],
                range: wordLimit.range,
                targetString: `${wordLimit.range[0]}-${wordLimit.range[1]} Wörter`
            };
        }

        // Handle detailed type (for complex requirements)
        if (wordLimit.type === 'detailed') {
            return {
                type: 'detailed',
                target: 0,
                range: [0, 0],
                targetString: ' (Detaillierte Antwort erforderlich)'
            };
        }

        // Handle OSD-style wordLimit that might just be a number
        if (typeof wordLimit === 'number') {
            return {
                type: 'approx',
                target: wordLimit,
                range: [wordLimit * 0.9, wordLimit * 1.1],
                targetString: `ca. ${wordLimit} Wörter`
            };
        }

        // Handle string-based word limits (e.g., "circa 120 Wörter")
        if (typeof wordLimit === 'string') {
            const numbers = wordLimit.match(/\d+/g);
            if (numbers && numbers.length > 0) {
                const target = parseInt(numbers[0]);
                return {
                    type: 'approx',
                    target: target,
                    range: [target * 0.9, target * 1.1],
                    targetString: wordLimit
                };
            }
        }

        return defaults;
    }, [wordLimit]);

    // If no valid word limit was found, render a simple counter
    if (parsedLimit.type === 'none' || parsedLimit.type === 'detailed') {
        return (
            <div className="text-sm font-medium text-gray-500 my-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                Wörter: <span className="font-bold text-gray-800">{currentCount}</span>
                {parsedLimit.targetString}
            </div>
        );
    }

    // Determine the visual style based on progress
    const getProgressStyle = () => {
        const { type, target, range } = parsedLimit;
        let percentage = 0;
        let colorClass = 'bg-gray-300';
        let textColorClass = 'text-gray-600 bg-gray-100';

        switch (type) {
            case 'min':
                percentage = Math.min((currentCount / target) * 100, 100);
                if (currentCount >= target) {
                    colorClass = 'bg-green-500';
                    textColorClass = 'text-green-800 bg-green-100';
                } else if (currentCount > 0) {
                    colorClass = 'bg-blue-500';
                    textColorClass = 'text-blue-800 bg-blue-100';
                }
                break;

            case 'approx':
            case 'range':
                const [lowerBound, upperBound] = range;
                if (currentCount > upperBound) {
                    percentage = 100;
                    colorClass = 'bg-red-500';
                    textColorClass = 'text-red-800 bg-red-100';
                } else if (currentCount >= lowerBound) {
                    percentage = 100;
                    colorClass = 'bg-green-500';
                    textColorClass = 'text-green-800 bg-green-100';
                } else if (currentCount > 0) {
                    percentage = (currentCount / lowerBound) * 100;
                    colorClass = 'bg-yellow-500';
                    textColorClass = 'text-yellow-800 bg-yellow-100';
                }
                break;
            default:
                break;
        }
        return { width: `${Math.max(0, Math.min(percentage, 100))}%`, colorClass, textColorClass };
    };

    const progressStyle = getProgressStyle();

    return (
        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex justify-between items-center text-sm font-semibold mb-2">
                <span className="flex items-center text-gray-700">
                    <Target className="w-4 h-4 mr-2" />
                    Wortanzahl
                </span>
                <span className={`font-mono px-2 py-0.5 rounded-md text-xs sm:text-sm transition-colors ${progressStyle.textColorClass}`}>
                    {currentCount} / {parsedLimit.targetString}
                </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                    className={`h-2 rounded-full transition-all duration-300 ${progressStyle.colorClass}`}
                    style={{ width: progressStyle.width }}
                ></div>
            </div>
        </div>
    );
};

export default WritingWordCounter;